import { interpolate, smoothInterpolate, calculateRelationDetailsForVectors, toDegrees } from "../utils/math";
import { Vector3, Vector2, Euler } from "three";
import { ObjectRelation, AngleRelation, AudioMode } from "./Types";
import { clamp } from "three/src/math/MathUtils";
import { INVERTED_LOWPASS_CONFIG, DEFAULT_LOWPASS_CONFIG, DEFAULT_VOLUME_CONFIG, DEFAULT_PANNING_CONFIG, getAudioConfig, innerConeAngleRadiansForDistance, AudioConfig, DEFAULT_AUDIO_CONFIG, LOWPASS_VALUE_AT_DISTANCE } from "../utils/audio-config";
import { Sound } from "./Sound";
import UserMediaService from "../services/UserMediaService";

export class UserAudio {
  sound: Sound;
  lowPassFilter: BiquadFilterNode;
  distanceGain: GainNode;
  sourceAngleGain: GainNode;
  targetAngleGain: GainNode;
  panner: StereoPannerNode;
  config: AudioConfig;

  constructor(trackOrStreamOrSrc: MediaStreamTrack | MediaStream | string) {
    this.sound = new Sound(trackOrStreamOrSrc);

    const audioContext = UserMediaService.getAudioContext();
    this.lowPassFilter = this.createLowPassFilter(audioContext);
    this.distanceGain = audioContext.createGain();
    this.sourceAngleGain = audioContext.createGain();
    this.targetAngleGain = audioContext.createGain();
    this.panner = audioContext.createStereoPanner();

    this.applyAudioFilters(audioContext);
    this.config = DEFAULT_AUDIO_CONFIG;
  }

  public updateAudioConfig(audioConfig: AudioConfig) {
    this.config = audioConfig;
  }

  public updateAudioFilters(sourcePosition: Vector3, sourceRotation: Euler, targetPosition: Vector3, targetRotation: Euler, options = { lowpass: true, distance: true, source: true, target: true, pan: true }) {
    const vectorRelations = calculateRelationDetailsForVectors(sourcePosition, sourceRotation, targetPosition, targetRotation);
    if (options.lowpass) this.updateLowPassFilter(vectorRelations);
    if (options.distance) this.updateDistanceGain(vectorRelations);
    if (options.source) this.updateSourceAngleGain(vectorRelations);
    if (options.target) this.updateTargetAngleGain(vectorRelations);
    if (options.pan) this.updatePan(vectorRelations);
    // this.logAudioFilters(sourcePosition, sourceRotation, targetPosition, targetRotation);
  }

  public logAudioFilters(s1p?: Vector3, s1r?: Euler, tp?: Vector3, tr?: Euler) {
    const mad1 = !s1p || !s1r || !tp || !tr ? undefined : calculateRelationDetailsForVectors(s1p, s1r, tp, tr);
    if (this.sourceAngleGain && this.targetAngleGain && this.distanceGain) {
      const totalGain = this.distanceGain.gain.value * this.sourceAngleGain.gain.value * this.targetAngleGain.gain.value;
      const madMsg = !mad1 ? '' : `, dS: ${mad1.distance.toFixed(1)}, θS: ${toDegrees(mad1.sourceToTarget.clockwiseAngleRadians).toFixed(1)}, `;
      console.log(`${madMsg}lPF: ${this.lowPassFilter?.frequency.value.toFixed(0)}, FV: ${totalGain.toFixed(3)}, dV: ${this.distanceGain?.gain.value.toFixed(3)}, θV: ${this.sourceAngleGain?.gain.value.toFixed(3)}, tV: ${this.targetAngleGain?.gain.value.toFixed(3)} - pan: ${this.panner?.pan.value.toFixed(3)}`);
    }
  }

  private updateLowPassFilter(vectorRelations: ObjectRelation) {
    if (!this.lowPassFilter) return;
    this.lowPassFilter.frequency.value = this.lowPassFn(vectorRelations);
  }

  private lowPassFn({ distance, sourceToTarget, targetToSource}: ObjectRelation) {
    const distanceRange = new Vector2(LOWPASS_VALUE_AT_DISTANCE[0].x, LOWPASS_VALUE_AT_DISTANCE[LOWPASS_VALUE_AT_DISTANCE.length - 1].x);
    const valueRange = new Vector2(LOWPASS_VALUE_AT_DISTANCE[0].y, LOWPASS_VALUE_AT_DISTANCE[LOWPASS_VALUE_AT_DISTANCE.length - 1].y);
    if (!this.lowPassFilter) return valueRange.y;
    if (distance < distanceRange.x) {
      return valueRange.x;
    } else {
      const transformDistanceAndInterpolateForRange = (distance: number) => {
        const effectiveInnerConeAngleRadians = interpolate(distance, distanceRange.x, this.config.source.innerConeMaxDistance, this.config.source.innerConeAngleRadiansRange.x, this.config.source.innerConeAngleRadiansRange.y);
        if (sourceToTarget.shortestAngleRadians <= effectiveInnerConeAngleRadians) {
          const amplificationForInnerCone = interpolate(distance, distanceRange.x, this.config.source.innerConeMaxDistance, this.config.source.innerConeLowPassAmplificationRange.x, this.config.source.innerConeLowPassAmplificationRange.y);
          distance -= Math.min(amplificationForInnerCone, distance - distanceRange.x);
        }
        if (sourceToTarget.shortestAngleRadians > effectiveInnerConeAngleRadians) {
          const interpolatedDistanceForMaxPenaltyBeyondTable = interpolate(distance, 2 * this.config.tableRadius, Math.max(distanceRange.y, 2 * this.config.tableRadius + distanceRange.y - this.config.source.outerConeLowPassReductionRange.y), this.config.source.outerConeLowPassReductionRange.y, distanceRange.y);
          const interpolatedDistanceForMaxPenaltyWithinTable = interpolate(distance, LOWPASS_VALUE_AT_DISTANCE[0].x, LOWPASS_VALUE_AT_DISTANCE[LOWPASS_VALUE_AT_DISTANCE.length-1].x, this.config.source.outerConeLowPassReductionRange.x, this.config.source.outerConeLowPassReductionRange.y);
          const isDistanceBeyondTable = this.config.tableRadius > 0 && distance > 2 * this.config.tableRadius;
          distance = interpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians, this.config.outerConeAngleRadians, distance, isDistanceBeyondTable ? interpolatedDistanceForMaxPenaltyBeyondTable : interpolatedDistanceForMaxPenaltyWithinTable);
        }
        if (targetToSource.shortestAngleRadians > this.config.target.innerConeAngleRadians) {
          distance += interpolate(targetToSource.shortestAngleRadians, this.config.target.innerConeAngleRadians, this.config.outerConeAngleRadians, this.config.target.outerConeLowPassReductionRange.x, this.config.target.outerConeLowPassReductionRange.y);
        }
        let i = 0;
        while (LOWPASS_VALUE_AT_DISTANCE[i].x < distance && i < LOWPASS_VALUE_AT_DISTANCE.length - 1) {
          i++;
        }
        return i === 0 ? LOWPASS_VALUE_AT_DISTANCE[0].y : interpolate(distance, LOWPASS_VALUE_AT_DISTANCE[i-1].x, LOWPASS_VALUE_AT_DISTANCE[i].x, LOWPASS_VALUE_AT_DISTANCE[i-1].y, LOWPASS_VALUE_AT_DISTANCE[i].y);
      }
      const interpolVal = transformDistanceAndInterpolateForRange(distance);
      return clamp(interpolVal, this.lowPassFilter.frequency.minValue, this.lowPassFilter.frequency.maxValue);
    }
  }

  private updateDistanceGain({ distance, sourceToTarget }: ObjectRelation) {
    if (!this.distanceGain) return;
    this.distanceGain.gain.value = this.defaultDistanceGainFn(distance);
  }

  private defaultDistanceGainFn(distance: number) {
    let effectiveDistance = Math.max(0, distance);
    const updatedVolume = 1 / (1 + effectiveDistance * effectiveDistance * 0.25);
    return updatedVolume;
  }

  private updateSourceAngleGain({ distance, sourceToTarget }: ObjectRelation) {
    if (!this.sourceAngleGain) return;
    const distanceMinRange = 1;
    // let effectiveDistance = Math.max(0, distance - distanceMinRange);
    const effectiveInnerConeAngleRadians = interpolate(distance, distanceMinRange, this.config.source.innerConeMaxDistance, this.config.source.innerConeAngleRadiansRange.x, this.config.source.innerConeAngleRadiansRange.y);
    // const effectiveInnerConeAngleRadians = innerConeAngleRadiansForDistance(effectiveDistance);
  
    const isClose = distance <= distanceMinRange;
    const isFacingSource = sourceToTarget.shortestAngleRadians <= effectiveInnerConeAngleRadians
  
    if (isClose && isFacingSource) this.sourceAngleGain.gain.value = 1;
    else if (isClose) this.sourceAngleGain.gain.value = interpolate(distance, 0, distanceMinRange, 0.6, 1);
    else if (!isClose && isFacingSource) {
      const amplification = interpolate(distance, distanceMinRange, this.config.source.innerConeMaxDistance, this.config.source.innerConeVolumeAmplificationRange.x, this.config.source.innerConeVolumeAmplificationRange.y );
      const bufferCoefficient = 0.2;
      const bufferForInnerCone = interpolate(distance, distanceMinRange, this.config.source.innerConeMaxDistance, this.config.source.innerConeAngleRadiansRange.x * bufferCoefficient, this.config.source.innerConeAngleRadiansRange.y * bufferCoefficient);
      const smoothAmpllification = interpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians - bufferForInnerCone, effectiveInnerConeAngleRadians, amplification, 0);
      this.sourceAngleGain.gain.value = 1 + smoothAmpllification;
    }
    else if (!isFacingSource) {
      let interpolationValue = smoothInterpolate(sourceToTarget.shortestAngleRadians, effectiveInnerConeAngleRadians, this.config.outerConeAngleRadians, this.config.source.outerConeVolumeReductionRange.x, this.config.source.outerConeVolumeReductionRange.y, effectiveInnerConeAngleRadians + this.config.outerConeBufferForMinValue, 0);
      this.sourceAngleGain.gain.value = 1 - interpolationValue;
    }
  }

  private updateTargetAngleGain({ distance, targetToSource }: ObjectRelation) {
    if (!this.targetAngleGain) return;
    const isFacingSource = targetToSource.shortestAngleRadians <= this.config.target.innerConeAngleRadians || distance <= 0.2;
  
    if (isFacingSource) this.targetAngleGain.gain.value = 1;
    else if (!isFacingSource) {
      let interpolationValue = smoothInterpolate(targetToSource.shortestAngleRadians, this.config.target.innerConeAngleRadians, this.config.outerConeAngleRadians, this.config.target.outerConeVolumeReductionRange.x, this.config.target.outerConeVolumeReductionRange.y, this.config.target.innerConeAngleRadians + this.config.outerConeBufferForMinValue, 0);
      this.targetAngleGain.gain.value = 1 - interpolationValue;
    }
  }

  private updatePan({ distance, sourceToTarget }: ObjectRelation) {
    if (!this.panner) return;
    const config = DEFAULT_PANNING_CONFIG;
    const effectiveDistance = Math.max(0, distance - config.minRange);
    const gradient = (config.maxValue - config.minValue) / (config.maxRange - config.minRange);
    const panningCoefficient = Math.min(config.maxValue, gradient * effectiveDistance + config.minValue); // y = mx + c with a ceiling
    const panValue = Math.sin(sourceToTarget.clockwiseAngleRadians) * panningCoefficient;
    // console.log('distance: ' , distance, ' pan: ' + attemptOne + ' -- ');
    this.panner.pan.value = panValue;
  }

  public applyAudioFilters(audioContext: AudioContext) {
    if (this.lowPassFilter && this.distanceGain && this.sourceAngleGain && this.targetAngleGain && this.panner) {
      this.sound.source.connect(this.distanceGain);
      this.distanceGain.connect(this.sourceAngleGain);
      this.sourceAngleGain.connect(this.targetAngleGain);
      this.targetAngleGain.connect(this.lowPassFilter);
      this.lowPassFilter.connect(this.panner);
      this.panner.connect(audioContext.destination);
    } else {
      this.removeAudioFilters(audioContext);
    }
  }

  public disconnect(audioContext: AudioContext) {
    this.removeAudioFilters(audioContext);
    this.sound.disconnect();
  }

  private createLowPassFilter(audioContext: AudioContext) {
    const lowPassFilter = audioContext.createBiquadFilter();
    lowPassFilter.type = 'lowpass';
    lowPassFilter.frequency.value = lowPassFilter.frequency.maxValue;
    return lowPassFilter;
  }

  private removeAudioFilters(audioContext: AudioContext) {
    this.sound.source.connect(audioContext.destination);
  }
}
